import React from 'react';

interface PairUpLogoProps {
  className?: string;
  size?: number | string;
  showText?: boolean;
  textColor?: 'dark' | 'light';
  tagline?: boolean;
}

/**
 * PairUpToday Official Brand Logo Emblem (Heartlike Partnership Emblem)
 * Two companion figures (blue & magenta) forming an elegant heart silhouette,
 * with a center red/pink heart and ponytail detail. Clean, modern, warm emblem.
 */
export const PairUpLogo: React.FC<PairUpLogoProps> = ({
  className = 'w-10 h-10',
  size,
  showText = false,
  textColor = 'dark',
  tagline = false,
}) => {
  const isLight = textColor === 'light';

  const svgEmblem = (
    <svg
      viewBox="0 0 500 500"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={size ? undefined : className}
      style={size ? { width: size, height: size } : undefined}
      aria-label="PairUpToday Logo"
    >
      <defs>
        {/* Left Companion (Blue) Gradient */}
        <linearGradient id="pul-blue-body" x1="100" y1="180" x2="260" y2="450" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#0088FF" />
          <stop offset="35%" stopColor="#0066FF" />
          <stop offset="70%" stopColor="#004AD6" />
          <stop offset="100%" stopColor="#0035A8" />
        </linearGradient>

        {/* Blue Head Radial Gradient */}
        <radialGradient id="pul-blue-head" cx="38%" cy="32%" r="65%">
          <stop offset="0%" stopColor="#47B0FF" />
          <stop offset="45%" stopColor="#0077FF" />
          <stop offset="85%" stopColor="#0050E6" />
          <stop offset="100%" stopColor="#0037B3" />
        </radialGradient>

        {/* Right Companion (Pink / Magenta) Gradient */}
        <linearGradient id="pul-pink-body" x1="400" y1="180" x2="240" y2="450" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FF3388" />
          <stop offset="35%" stopColor="#FF156D" />
          <stop offset="70%" stopColor="#D9005B" />
          <stop offset="100%" stopColor="#A80045" />
        </linearGradient>

        {/* Pink Head Radial Gradient */}
        <radialGradient id="pul-pink-head" cx="38%" cy="32%" r="65%">
          <stop offset="0%" stopColor="#FFA8CE" />
          <stop offset="45%" stopColor="#FF2E85" />
          <stop offset="85%" stopColor="#D90060" />
          <stop offset="100%" stopColor="#9C0042" />
        </radialGradient>

        {/* Center Floating Heart Gradient */}
        <linearGradient id="pul-center-heart" x1="235" y1="170" x2="265" y2="215" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FF4075" />
          <stop offset="50%" stopColor="#F2004B" />
          <stop offset="100%" stopColor="#BD0036" />
        </linearGradient>

        {/* Elevation Shadow */}
        <filter id="pul-shadow" x="-8%" y="-8%" width="116%" height="116%" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="5" stdDeviation="8" floodColor="#0A1833" floodOpacity="0.16" />
        </filter>
      </defs>

      <g filter="url(#pul-shadow)">
        {/* 1. Left Companion (Blue) Body forming Left Half of Heart */}
        <path
          d="M 195 180
             C 150 185 102 214 86 262
             C 68 316 88 376 142 414
             C 182 442 228 458 250 464
             C 232 428 202 390 182 350
             C 160 306 162 262 206 238
             C 226 227 244 242 248 248
             C 232 228 216 206 208 188
             C 203 182 199 180 195 180 Z"
          fill="url(#pul-blue-body)"
        />

        {/* 2. Blue Inner Body Shading (Glossy 3D depth) */}
        <path
          d="M 250 464
             C 228 426 196 386 178 342
             C 158 294 166 252 212 234
             C 230 226 244 238 248 244
             C 230 256 198 274 188 316
             C 178 358 210 416 250 464 Z"
          fill="#0091FF"
          opacity="0.45"
        />

        {/* 3. Right Companion (Pink / Magenta) Body forming Right Half of Heart */}
        <path
          d="M 305 180
             C 350 185 398 214 414 262
             C 432 316 412 376 358 414
             C 318 442 272 458 250 464
             C 268 428 298 390 318 350
             C 340 306 338 262 294 238
             C 274 227 256 242 252 248
             C 268 228 284 206 292 188
             C 297 182 301 180 305 180 Z"
          fill="url(#pul-pink-body)"
        />

        {/* 4. Pink Inner Body Shading (Glossy 3D depth) */}
        <path
          d="M 250 464
             C 272 426 304 386 322 342
             C 342 294 334 252 288 234
             C 270 226 256 238 252 244
             C 270 256 302 274 312 316
             C 322 358 290 416 250 464 Z"
          fill="#FF4D94"
          opacity="0.45"
        />

        {/* 5. Blue Companion Head (Left) */}
        <circle cx="180" cy="136" r="48" fill="url(#pul-blue-head)" />

        {/* 6. Pink Companion Head (Right) */}
        <circle cx="320" cy="144" r="45" fill="url(#pul-pink-head)" />

        {/* 7. Feminine Ponytail on Pink Companion */}
        <path
          d="M 348 122
             C 370 106 398 104 408 116
             C 418 128 408 152 404 170
             C 400 188 404 198 412 202
             C 408 203 400 196 396 185
             C 390 167 394 149 386 139
             C 378 131 362 133 348 137 Z"
          fill="url(#pul-pink-head)"
        />

        {/* 8. Center Floating Little Heart (Between Leaning Heads) */}
        <path
          d="M 250 178
             C 246 166 232 164 227 175
             C 221 187 233 199 250 212
             C 267 199 279 187 273 175
             C 268 164 254 166 250 178 Z"
          fill="url(#pul-center-heart)"
        />
      </g>
    </svg>
  );

  if (!showText) {
    return svgEmblem;
  }

  return (
    <div className="flex items-center gap-2.5">
      {svgEmblem}
      <div className="flex flex-col">
        <div className="flex items-center tracking-tight leading-none">
          <span className={`font-extrabold text-xl font-sans ${isLight ? 'text-white' : 'text-slate-900'}`}>
            PairUp
          </span>
          <span className="font-extrabold text-xl text-pink-600 font-sans ml-0.5">
            Today
          </span>
        </div>
        {tagline && (
          <span className={`text-[10px] font-semibold mt-0.5 tracking-wide ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
            Find Your Perfect Companion
          </span>
        )}
      </div>
    </div>
  );
};
